import { useEffect, useRef, useState } from "react";
import { DownChevronIcon, SendIcon } from "../Svg";
import { Button } from "../Button";
import _ from "lodash";
import cx from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { SelectInput } from "../form/SelectInput";
import UIState, { selectMobileLayout } from "../../store/UIState";
import { createSelector } from "reselect";
import { ModalTypes, PredictionStatus } from "../../constants";
import { ShowcaseSettingsPanel } from "./ShowcaseSettingsPanel";
import { AiOndemandApi, PREDICTION_STARTED, PREDICTION_STOPPED, selectApiKeys, selectPredictionId, selectPredictionStatus } from "../../store/models/AiOndemandApi";
import { getOndemandErrorMsg, extractErrorText } from "../../utils";
import { selectCurrentUser } from "../../store/models/User";
import { Kokoro82M, VoiceChoices } from "../../constants/ondemandApi";
import Loader from "../Loader";


const AudioMessage = ({ message }) => {
  const error = message.error;
  const audioUrl = message.audioData?.path;
  return (
    <div className={cx("ResultBox")}>
      {error ? <div className="ResultBox__Error">
        {error}
      </div> : <div className={"ResultBox__Text"}>
        <div className={"ResultBox__Text--label"}>
          Audio generation complete. Here is the result:
        </div>
        <audio controls>
          <source src={audioUrl} type="audio/mpeg" />
          Your browser does not support the audio element.
        </audio>
      </div>}
    </div>)
}

const AudioLoadingItem = ({ elapsedTime, predictionStatus }) => {
  const isLoadingPrediction = predictionStatus === PredictionStatus.IN_PROGRESS
    || predictionStatus === PredictionStatus.STARTED;

  if (isLoadingPrediction) {
    return (<div className={cx("ResultBox")}>
      <div className={"ResultBox__Loading"}>
        <div className={"ResultBox__Loading__TopWrapper"}>
          Processing | {elapsedTime}s
        </div>
        <div className={"ResultBox__Loading__CenterWrapper"}>
          <Loader color={"green"} />
        </div>
        <div className={"ResultBox__Loading__BottomWrapper"}>Audio generation in progress...</div>
      </div>
    </div>)
  }
}

const TextItem = ({ message, elapsedTime }) => {

  const [paramsOpened, setParamsOpened] = useState(false);

  const onToggleParams = () => {
    setParamsOpened(!paramsOpened);
  }

  return (
    <div className={cx("UserMessageContainer")}>
      <div className="UserMessage">
        <div className={"UserMessage__PromptLabel"}>
          Text
        </div>
        <div className={"UserMessage__PromptValue"}>{message.text}</div>
        <div className={"UserMessage__PromptLabel"}>
          Voice
        </div>
        <div className={"UserMessage__PromptValue"}>{VoiceChoices[message.voice]}</div>
        <div className={"UserMessage__BottomWrapper"}>
          <div className={`UserMessage__ParamsButton ${paramsOpened && "open"}`}
            onClick={onToggleParams}>More parameters <DownChevronIcon /></div>
        </div>
        {paramsOpened &&
          <div className={"UserMessage__Params"}>
            <pre>{JSON.stringify({ prompt: message.text, ...message.params }, null, 2)}</pre>
          </div>}
      </div>

    </div>)
}

const selector = createSelector(
  selectMobileLayout,
  selectApiKeys,
  selectPredictionStatus,
  selectPredictionId,
  selectCurrentUser,
  (isMobile, apiKeys, predictionStatus, predictionId, curUser) => ({
    isMobile,
    apiKeys,
    predictionStatus,
    predictionId,
    curUser
  }
  ))

let intervalId = null;

export const ShowcaseTextToAudio = ({ className, mName = null }) => {
  const dispatch = useDispatch();
  const {
    isMobile,
    apiKeys,
    predictionStatus,
    predictionId,
    curUser
  } = useSelector(state => selector(state));
  const currentModel = Kokoro82M;
  const apiKey = _.get(apiKeys, '0.key');
  const [textareaValue, setTextareaValue] = useState('');
  const [messages, setMessages] = useState([]);
  const chatboxRef = useRef(null);
  const [showSettings, setShowSettings] = useState(!isMobile);
  const [settings, setSettings] = useState({});
  const [changeSeed, setChangeSeed] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [voiceOptions, SetVoiceOptions] = useState(Object.keys(VoiceChoices).map(k => ({ label: VoiceChoices[k], value: k })));
  const [voice, setVoice] = useState({ label: VoiceChoices.af_sky, value: 'af_sky' });

  useEffect(() => {
    return async () => {
      if (intervalId) {
        clearInterval(intervalId)
      }
    }
  }, []);

  useEffect(() => {
    if (!mName) return;
    if (messages.length > 2) {
      onScrollToPageBottom();
    }
  }, [messages])

  const handleSubmit = async (event) => {
    if (predictionStatus === PredictionStatus.IN_PROGRESS) {
      onStopGenerating(event);
    } else {
      sendMessage(event);
    }
  }

  const onStopGenerating = (event) => {
    event.preventDefault();

    dispatch(UIState.actions.showModal(ModalTypes.CONFIRM, {
      title: "Stop generating",
      message: "Are you sure you want to stop the ongoing generation?",
      onConfirm: onConfirmStopGenerating,
      confirmLabel: "Yes",
      cancelLabel: "No",
      size: 's'
    }))
  }

  const onConfirmStopGenerating = async () => {
    stopInterval();
    try {
      setMessages((messages) => messages.slice(0, -2))
      if (mName) {
        await dispatch(AiOndemandApi.actions.stopHostedPredict(predictionId, apiKey));
        toast.success("Stopped the generation process.")
      }
    } catch (e) {
      toast.success(`Failed to stop the generation. Error: ${e.message}`)
    }
  }


  const startStopwatch = () => {
    if (intervalId) {
      stopInterval()
    }
    const startTime = new Date().getTime()
    intervalId = setInterval(() => {
      const currentTime = new Date().getTime()
      setElapsedTime(((currentTime - startTime) / 1000).toFixed(0))
    }, 1000)

  }

  const stopInterval = () => {
    clearInterval(intervalId)
    intervalId = null
    setElapsedTime(0)
  }

  const sendMessage = async (event) => {
    event.preventDefault();

    if (mName) setTextareaValue('');

    const userMsg = { text: textareaValue, voice: voice.value, params: settings, type: 'prompt' };
    const botMsg = { type: 'loading' }
    const newMessages = [...messages, userMsg, botMsg];

    setMessages(newMessages)
    const message = { text: textareaValue, ...settings, voice: voice.value };

    generateAudio(message, newMessages.length - 1)

    setTimeout(() => {
      if (!_.isNil(chatboxRef) && !_.isNil(chatboxRef.current)) {
        chatboxRef.current.scrollTop = chatboxRef.current.scrollHeight;
      }
    }, 1000)
  };

  const generateAudio = async (message, index) => {
    try {
      let audio;
      if (mName) {
        startStopwatch();
        let result = await dispatch(AiOndemandApi.actions.startHostedPredict(message, mName, apiKey));
        audio = _.get(result, 'body.infer_requests.0.output.audio_url')
        stopInterval();
      }
      if (!audio) return;
      setMessages(oldMessages => {
        const newMessages = [...oldMessages];
        newMessages[index].audioData = { prompt: message.text, path: audio };
        newMessages[index].type = 'result';
        return newMessages
      });
    } catch (e) {
      const errMsg = getOndemandErrorMsg(e.message, curUser);
      const toastErr = extractErrorText(errMsg);
      toast.error(toastErr)
      dispatch({ type: PREDICTION_STOPPED })
      stopInterval()
      setMessages(oldMessages => {
        const newMessages = [...oldMessages];
        newMessages[index].type = 'result';
        newMessages[index].error = errMsg;
        return newMessages
      });
    }
  }
  const onTextareaChange = (event) => {
    setTextareaValue(event.target.value)
  }

  const onScrollToBottom = () => {
    chatboxRef.current.scrollTop = chatboxRef.current.scrollHeight;
  }

  const onScrollToTop = () => {
    chatboxRef.current.scrollTop = 0;
  }

  const onScrollToPageBottom = () => {
    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
  };

  const onToggleSettings = () => {
    setShowSettings(!showSettings);
  }

  const onSettingsChanged = (settings) => {
    setSettings(settings)
  }

  return (
    <div className={cx("ShowcaseTextToAudio", className)}>
      <div className="ShowcaseTextToAudio__container">

        <div className={"ShowcaseTextToAudio__SidePanels"}>
          <div className={"SidePanel"}>
            {!isMobile && <div className={"ShowcaseTextToAudio__LeftInfo"}>
              <div className={"SidePanel__Title"}>Model</div>
              <SelectInput className={"SidePanel__Select"}
                value={{ label: 'Kokoro 82M', value: 'Kokoro 82M' }}
                isDisabled={!!mName}
              />
            </div>}

            <div className={"SidePanel__Header"}>
              <div className={"SidePanel__Header__Prompt"}>Text</div>
            </div>
            <form className="SidePanel__form" onSubmit={handleSubmit}>
              <textarea name="message"
                className="SidePanel__input"
                value={textareaValue}
                onChange={onTextareaChange}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    sendMessage(event);
                  }
                }}
                placeholder="Eg. This is a demo of the Kokoro-82M text-to-speech model."
                disabled={predictionStatus !== PredictionStatus.STOPPED}
              />
              <div className={"SidePanel__Header"}>
                <div className={"SidePanel__Header__Prompt"}>Voice</div>
              </div>
              <SelectInput className={"SidePanel__Select"}
                options={voiceOptions}
                value={voice}
                onChange={setVoice}
                styles={customStyles}
              />

              {predictionStatus === PredictionStatus.IN_PROGRESS
                ? <Button
                  title={"Stop generating"}
                  type="submit"
                  className="ShowcaseTextToAudio__button"
                  color={"red"} />
                : <Button
                  title={"Send"}
                  type="submit"
                  icon={predictionStatus !== PredictionStatus.STARTED && <SendIcon />}
                  className="ShowcaseTextToAudio__button"
                  color={"green"}
                  size={"small"}
                  loading={predictionStatus === PredictionStatus.STARTED}
                  disabled={_.isEmpty(textareaValue) || predictionStatus === PredictionStatus.STARTED} />}
            </form>

            <div className={`SidePanel__OutputOptions ${showSettings && "open"}`}
              onClick={onToggleSettings}>Output options <DownChevronIcon /></div>

            {showSettings && <>
              {isMobile && <div className={"ShowcaseTextToAudio__LeftInfo"}>
                <div className={"SettingsPanel__Title"}>Model</div>
                <SelectInput className={"SidePanel__Select"}
                  value={{ label: 'Kokoro 82M', value: 'Kokoro 82M' }}
                  isDisabled={!!mName} />
              </div>}
              <ShowcaseSettingsPanel model={currentModel} onParamsChanged={onSettingsChanged} changeSeed={changeSeed} />
            </>}
          </div>
        </div>

        <div className={cx("ShowcaseTextToAudio__RightSide")}>
          {!_.isEmpty(messages) && <div className={"MessagesWrapper"} ref={chatboxRef}>
            {messages.map((item, index) => {
              switch (item.type) {
                case 'result':
                  return (<AudioMessage key={index} message={item} />)
                case 'loading':
                  return (<AudioLoadingItem key={index} elapsedTime={elapsedTime} predictionStatus={predictionStatus} />)
                case 'prompt':
                  return (<TextItem key={index} message={item} />)
              }
            })}
          </div>
          }
        </div>
      </div>

    </div>
  );

}

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: '#131722',
    borderColor: state.isFocused ? '#18C99D' : '#3D4463',
    borderRadius: '6px',
    color: '#8A8FB5',
    outline: 'none',
    boxShadow: 'none',
    ':hover': {
      borderColor: '#18C99D', // Add your hover styles here
    },
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? 'rgba(24, 201, 157, 0.1)' : '#131722',
    color: state.isFocused ? '#18C99D' : state.isSelected ? '#18C99D' : '#8A8FB5',
    fontFamily: 'Gilroy',
    paddingLeft: '10px',
    fontWeight: '500',
    fontSize: '14px',
    paddingTop: '11.5px',
    paddingBottom: '11.5px',
    position: 'relative',
  }),
  singleValue: (provided, state) => ({
    ...provided,
    marginLeft: '7px',
    borderColor: state.isFocused ? '#18C99D' : '#3D4463',
    color: '#8A8FB5',
    fontFamily: 'Gilroy',
    fontWeight: '500',
    fontSize: '14px',
    height: '15px',
    outline: 'none',
  }),
  indicatorsContainer: (provided, { isDisabled }) => ({
    ...provided,
    display: isDisabled ? 'none' : 'flex',
  }),
  indicatorSeparator: (provided, state) => ({
    ...provided,
    display: 'none'
  }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    color: '#8A8FB5',
    marginRight: '8px',
    ':hover': {
      color: '#8A8FB5', // Add your hover styles here
    },
  }),
  menu: (provided, state) => ({
    ...provided,
    backgroundColor: '#131722',
    border: '1px solid #3D4463',
    borderRadius: '6px',
    scrollbarColor: '#3D4463',
    maxHeight: '300px',
  }),
  menuList: (provided, state) => ({
    ...provided,
    maxHeight: '300px',
  }),
}